import { CustomPopover } from "@/components/popover/CustomPopover";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "@/pages/requests/cash/schema/handler-functions";
import {
  EntityStatusOption,
  getStatusByEntity,
} from "@/services/entity-status.service";
import {
  CampanaProductiva,
  getCampanasProductivasActivas,
  getGranosProductivos,
  GranoProductivo,
} from "@/services/productive-entities.service";
import { EntityTypes } from "@/constants/enums";
import { AppColors } from "@/styles/colors";
import {
  Badge,
  Box,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import { Filter } from "lucide-react";
import DatePicker from "@/components/date-picker";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  disableApply?: boolean;
}

export const GrossMarginRequestFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  control,
  disableApply,
  submitFilters,
  handleSubmit,
  setOpenFilters,
  clearFilters,
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const [statusOptions, setStatusOptions] = useState<EntityStatusOption[]>([]);
  const [loadingStatusOptions, setLoadingStatusOptions] = useState<boolean>(false);

  const fetchStatusOptions = async () => {
    setLoadingStatusOptions(true);
    const response = await getStatusByEntity({
      Id: [EntityTypes.SolicitudMargenBruto],
    });
    setStatusOptions(response ?? []);
    setLoadingStatusOptions(false);
  };

  const [cultivos, setCultivos] = useState<GranoProductivo[]>([]);
  const [loadingCultivos, setLoadingCultivos] = useState<boolean>(false);

  const fetchCultivos = async () => {
    setLoadingCultivos(true);
    const response = await getGranosProductivos();
    setCultivos(response ?? []);
    setLoadingCultivos(false);
  };

  const [campanas, setCampanas] = useState<CampanaProductiva[]>([]);
  const [loadingCampanas, setLoadingCampanas] = useState<boolean>(false);

  const fetchCampanas = async () => {
    setLoadingCampanas(true);
    const response = await getCampanasProductivasActivas();
    setCampanas(response ?? []);
    setLoadingCampanas(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchCultivos();
      fetchCampanas();
      fetchStatusOptions();
    }
  }, [isActive]);

  const cultivoItems = useMemo(
    () => cultivos.map((c) => ({ label: c.nombre, value: String(c.id) })),
    [cultivos],
  );

  const campanaItems = useMemo(
    () => campanas.map((c) => ({ label: c.nombre, value: String(c.id) })),
    [campanas],
  );

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);

  const loadAccountOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      timeoutRef,
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      getOptions: (v) => getOptionsHandler({ inputValue: v }),
    });
  };

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter size={16} />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear || disableFilters}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>

              <Grid templateColumns="repeat(2, 1fr)" gap={4} alignItems="end">
                <GridItem maxW="300px">
                  <DatePicker
                    name="fecha"
                    labelText="Fecha"
                    control={control}
                    clearable
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem maxW="300px">
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Titular cuenta</Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control}
                      loadOptions={loadAccountOptions}
                      name="cuentaOrigen"
                      placeholder="Buscar..."
                      isLoading={loadingAccounts}
                      disabled={disableFilters || loadingFilters || loadingAccounts}
                      closeMenuOnSelect
                      isClearable
                      triggerWithLength
                    />
                  </Field.Root>
                </GridItem>

                <GridItem colSpan={2}>
                  <SelectComponent
                    name="cultivos"
                    items={cultivoItems}
                    labelText="Cultivos"
                    placeholder="Seleccionar"
                    control={control}
                    fullWidth
                    loading={loadingCultivos}
                    disabled={disableFilters || loadingFilters || loadingCultivos}
                    multiple
                    menuMaxHeight="250px"
                    closeOnSelect={false}
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <SelectComponent
                    name="campanas"
                    items={campanaItems}
                    labelText="Campaña"
                    placeholder="Seleccionar"
                    control={control}
                    fullWidth
                    loading={loadingCampanas}
                    disabled={disableFilters || loadingFilters || loadingCampanas}
                    multiple
                    menuMaxHeight="250px"
                    closeOnSelect={false}
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <SelectComponent
                    name="estados"
                    items={statusOptions.map((s) => ({
                      label: s.nombre,
                      value: s.ids?.[0] ?? "",
                    }))}
                    labelText="Estado"
                    placeholder="Seleccionar"
                    control={control}
                    fullWidth
                    loading={loadingStatusOptions}
                    disabled={disableFilters || loadingFilters || loadingStatusOptions}
                    multiple
                    menuMaxHeight="250px"
                    closeOnSelect={false}
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={loadingFilters || disableFilters || disableApply}
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
